import React, { useRef, useEffect, useState, useMemo } from 'react';
import Player from '@vimeo/player';
import Icon from '../AppIcon';
import { buildEmbedUrl } from '../../utils/videoUrlBuilder';

const getVimeoVideoId = (url) => {
  if (!url || typeof url !== 'string') return null;
  const regExp = /(?:vimeo\.com\/|player\.vimeo\.com\/video\/)(?:.*#|.*\/videos\/)?([0-9]+)/;
  const match = url.match(regExp);
  return match ? match[1] : null;
};

const VimeoPlayer = ({
  url,
  videoId,
  config = {},
  onTimeUpdate,
  onComplete,
  currentTime = 0,
  className = 'w-full h-full',
}) => {
  const iframeRef = useRef(null);
  const playerRef = useRef(null);
  const startTimeRef = useRef(currentTime);
  const onTimeUpdateRef = useRef(onTimeUpdate);
  const onCompleteRef = useRef(onComplete);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    onTimeUpdateRef.current = onTimeUpdate;
    onCompleteRef.current = onComplete;
  }, [onTimeUpdate, onComplete]);

  const finalVideoId = useMemo(() => {
    if (videoId) return String(videoId);
    return getVimeoVideoId(url);
  }, [videoId, url]);

  const embedUrl = useMemo(() => {
    if (!finalVideoId) return null;
    try {
      return buildEmbedUrl(
        'vimeo',
        { video_id: finalVideoId },
        {
          autoplay: config.autoplay || false,
          start: startTimeRef.current || 0,
          ...config,
        }
      );
    } catch (error) {
      console.error('❌ VimeoPlayer: Erro ao construir URL:', error);
      return null;
    }
  }, [finalVideoId, config]);

  useEffect(() => {
    startTimeRef.current = currentTime;
  }, [finalVideoId]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || !embedUrl) return;

    let cancelled = false;
    setIsLoading(true);
    setLoadError(null);

    const player = new Player(iframe);
    playerRef.current = player;

    const handleTimeUpdate = (data) => {
      if (cancelled) return;
      onTimeUpdateRef.current?.(data.seconds, data.duration);
    };

    const handleEnded = () => {
      if (cancelled) return;
      onCompleteRef.current?.();
    };

    player.on('timeupdate', handleTimeUpdate);
    player.on('ended', handleEnded);

    player
      .ready()
      .then(async () => {
        if (cancelled) return;
        if (startTimeRef.current > 0) {
          try {
            await player.setCurrentTime(startTimeRef.current);
          } catch {
            // seek inicial opcional
          }
        }
        setIsLoading(false);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('❌ VimeoPlayer: falha ao inicializar player:', err);
          setLoadError(err);
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
      player.off('timeupdate', handleTimeUpdate);
      player.off('ended', handleEnded);
      player.unload().catch(() => {});
      playerRef.current = null;
    };
  }, [embedUrl]);

  if (!embedUrl) {
    return (
      <div className={`${className} bg-black flex items-center justify-center`}>
        <div className="text-white text-center">
          <Icon name="AlertCircle" size={48} className="mx-auto mb-4" />
          <p>URL do Vimeo inválida</p>
          <p className="text-sm mt-2">Video ID: {finalVideoId || 'não encontrado'}</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={`${className} bg-black flex items-center justify-center`}>
        <div className="text-white text-center px-4">
          <Icon name="AlertCircle" size={48} className="mx-auto mb-4" />
          <p>Não foi possível carregar o player do Vimeo</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${className} relative bg-black`}>
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-black">
          <div className="flex flex-col items-center space-y-4 text-white">
            <div className="animate-spin">
              <Icon name="Loader2" size={40} />
            </div>
            <p>Carregando vídeo do Vimeo...</p>
          </div>
        </div>
      )}

      <iframe
        ref={iframeRef}
        src={embedUrl}
        className="w-full h-full"
        frameBorder="0"
        allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
        allowFullScreen
        title="Vimeo video player"
      />
    </div>
  );
};

export default VimeoPlayer;
